import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Lazily register plugins on first use to avoid work at module import time.
let _dayjsPluginsRegistered = false;
const ensureDayjsPlugins = (): void => {
  if (_dayjsPluginsRegistered) return;
  dayjs.extend(customParseFormat);
  dayjs.extend(isSameOrBefore);
  _dayjsPluginsRegistered = true;
};

/**
 * Helper to convert Date object to standard YYYY-MM-DD format
 * @param date - Date object to format
 */
export const formatDateFromDateTime = (date: Date): string => {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    console.warn('Invalid date provided to formatDateFromDateTime');
    return '';
  }
  return dayjsDate.format('YYYY-MM-DD');
};

/**
 * Helper to validate dateFrom is not after dateTo
 * @param dateFrom - Start date string (YYYY-MM-DD format)
 * @param dateTo - End date string (YYYY-MM-DD format)
 */
export const validateDateRange = (dateFrom: string | null, dateTo: string | null): boolean => {
  // Allow if either date is null/empty (partial selection)
  if (!dateFrom || !dateTo) {
    return true;
  }
  try {
    // Ensure required dayjs plugins are registered (lazy + idempotent).
    ensureDayjsPlugins();
    const fromDayjs = dayjs(dateFrom, 'YYYY-MM-DD', true);
    const toDayjs = dayjs(dateTo, 'YYYY-MM-DD', true);

    // Check if dates are valid
    if (!fromDayjs.isValid() || !toDayjs.isValid()) {
      console.warn('Invalid date format provided to validateDateRange');
      return false;
    }
    // Check if dateFrom is after dateTo
    return fromDayjs.isSameOrBefore(toDayjs, 'day');
  } catch (error) {
    console.error('Error validating date range:', error);
    return false;
  }
};

/**
 * Helper to ensure date selected is not in the future
 * @param date - Date object to validate
 */
export const isFutureDate = (date: Date): boolean => {
  if (!date) return false;
  const selectedDayjs = dayjs(date);
  const today = dayjs().endOf('day');
  return selectedDayjs.isAfter(today);
};
